// Constants for the ShipmentDashboard. Extracted from the dashboard component
// to keep the file focused on UI/data plumbing.

// Columns that cannot be reordered (drag-and-drop is disabled for them).
export const PROTECTED_COLUMNS = ['marker', 'actions'];

// CSS class names for each shipment status (used by the dashboard table CSS).
export const STATUS_COLORS = {
  'Empty in Customer': 'status-empty-in-customer',
  'Available for Pickup': 'status-available-for-pickup',
  'Need Appt': 'status-need-appt',
  'On the way to Customer': 'status-on-way-to-customer',
  'Important Customer': 'status-important-customer',
  'Delivered to Customer': 'status-delivered-to-customer',
  'In Yard': 'status-in-yard',
  'Container Returned': 'status-container-returned',
  'On the way to Return': 'status-on-way-to-return',
  'Delivered to Exam Location': 'status-delivered-to-exam',
  'New Shipment': 'status-new-shipment',
  'Empty Container in Yard': 'status-empty-container-yard',
  'On the way to Pickup': 'status-on-way-to-pickup',
  'Shipment Closed': 'status-shipment-closed',
  'Trouble': 'status-trouble',
};

// Default hex row-background colours per status (matches the user's reference
// legend). The dashboard layers a 40-alpha shade for dark mode.
export const DEFAULT_STATUS_ROW_COLORS = {
  'Empty in Customer': '#FFDAB9',
  'Available for Pickup': '#90EE90',
  'Need Appt': '#1a1a1a',
  'On the way to Customer': '#FFB6C1',
  'Important Customer': '#A0522D',
  'Delivered to Customer': '#FFC0CB',
  'In Yard': '#DDA0DD',
  'Container Returned': '#D3D3D3',
  'On the way to Return': '#AFEEEE',
  'Delivered to Exam Location': '#8B4513',
  'New Shipment': '#E6E6FA',
  'Empty Container in Yard': '#FFFFE0',
  'On the way to Pickup': '#228B22',
  'Shipment Closed': '#ADD8E6',
  'Trouble': '#FA8072',
};

export const STATUS_OPTIONS = Object.keys(STATUS_COLORS);

// Default column definitions for the imports dashboard table.
export const DEFAULT_COLUMNS = [
  { id: 'marker', label: '', visible: true, width: 6 },
  { id: 'important', label: '', visible: true, width: 24 },
  { id: 'actions', label: '', visible: true, width: 60 },
  { id: 'container_number', label: 'Container', visible: true, width: 95 },
  { id: 'container_size', label: 'Sz', visible: true, width: 35 },
  { id: 'ssl', label: 'SSL', visible: true, width: 45 },
  { id: 'chassis', label: 'Chassis', visible: true, width: 75 },
  { id: 'job_number', label: '#', visible: false, width: 50 },
  { id: 'pp_ctf', label: 'PP', visible: true, width: 40 },
  { id: 'status', label: 'Status', visible: false, width: 100 },
  { id: 'eta', label: 'ETA', visible: true, width: 50 },
  { id: 'appointment', label: 'Appt', visible: true, width: 55 },
  { id: 'delivery_date', label: 'Dlvr', visible: true, width: 50 },
  { id: 'lfd', label: 'LFD', visible: true, width: 50 },
  { id: 'overweight', label: 'OW', visible: true, width: 30 },
  { id: 'hazmat', label: 'HZ', visible: true, width: 30 },
  { id: 'mty_rdy', label: 'MTY', visible: true, width: 50 },
  { id: 'mty_return', label: 'Ret', visible: true, width: 50 },
  { id: 'return_location', label: 'RT', visible: true, width: 45 },
  { id: 'yard', label: 'Yard', visible: true, width: 55 },
  { id: 'customer', label: 'Customer', visible: true, width: 80 },
  { id: 'deliver_to', label: 'Deliver To', visible: true, width: 100 },
  { id: 'driver', label: 'Driver', visible: true, width: 60 },
  { id: 'terminal', label: 'Term', visible: true, width: 50 },
  { id: 'mbl', label: 'MBL', visible: false, width: 80 },
  { id: 'pin', label: 'PIN', visible: false, width: 50 },
  { id: 'customs_status', label: 'Customs', visible: false, width: 60 },
  { id: 'pickup_date', label: 'Pickup', visible: true, width: 55 },
  { id: 'docs', label: 'Doc', visible: true, width: 35 },
  { id: 'remarks', label: 'Notes', visible: true, width: 70 },
];

// Default sort order used when no user-defined status order is saved.
export const DEFAULT_STATUS_ORDER = [
  'Delivered to Customer',
  'On the way to Return',
  'Empty Container in Yard',
  'Empty in Customer',
  'On the way to Customer',
  'In Yard',
  'Delivered to Exam Location',
  'On the way to Pickup',
  'Trouble',
  'Available for Pickup',
  'Need Appt',
  'Important Customer',
  'New Shipment',
  'Container Returned',  // Hidden by default (closed)
  'Shipment Closed',     // Hidden by default (closed)
];

// Columns where right-click colour picking + inline edit are allowed.
export const COLOR_PICKABLE_COLUMNS = [
  'container_number', 'container_size', 'ssl', 'chassis', 'job_number',
  'customer', 'deliver_to', 'driver', 'remarks', 'mty_return',
];

// Date fields that get the T12:00:00 suffix on inline date saves so that
// timezone shifts don't accidentally bump the calendar day.
export const DATE_FIELDS = [
  'mty_rdy', 'mty_return', 'lfd', 'delivery_date', 'eta',
  'appointment_date', 'pickup_date', 'lfd_yard',
];

// Minimum column widths so the table never collapses into illegibility on
// narrow viewports. Used when DataTable computes flex widths.
export const MIN_COLUMN_WIDTHS = {
  'marker': '8px',
  'important': '30px',
  'actions': '80px',
  'container_number': '100px',
  'container_size': '45px',
  'ssl': '50px',
  'chassis': '60px',
  'job_number': '55px',
  'pp_ctf': '50px',
  'status': '100px',
  'eta': '55px',
  'appointment': '55px',
  'delivery_date': '60px',
  'lfd': '55px',
  'overweight': '35px',
  'hazmat': '35px',
  'mty_rdy': '65px',
  'mty_return': '65px',
  'return_location': '55px',
  'yard': '65px',
  'customer': '90px',
  'deliver_to': '120px',
  'driver': '65px',
  'terminal': '55px',
  'mbl': '90px',
  'pin': '55px',
  'customs_status': '70px',
  'pickup_date': '65px',
  'docs': '40px',
  'remarks': '90px',
};

// Statuses whose default background is dark enough to need white text in
// light mode. (Dark mode inherits the surrounding text colour.)
export const DARK_BACKGROUND_STATUSES = [
  'Need Appt',
  'Important Customer',
  'Delivered to Exam Location',
  'On the way to Pickup',
];
